<template>
  <div class="static-proxy-page dark-theme">
    <!-- Navigation -->
    <div class="nav-container" :class="{ 'scrolled': isScrolled }">
      <nav class="modern-nav">
        <div class="nav-brand" @click="$router.push('/')">
          <img src="/logo.png" alt="logo" class="brand-logo-img" />
          <span class="brand-name">spaceproxy</span>
        </div>

        <div class="nav-links desktop-only">
          <router-link to="/">{{ $t('menu.home') }}</router-link>
          <router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link>
          <router-link to="/static-residential-proxy" class="router-link-exact-active">{{ $t('menu.staticProxyPage') }}</router-link>
          <router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link>
        </div>

        <div class="nav-actions">
          <a-dropdown :trigger="['click']" placement="bottomCenter">
            <button class="action-icon-btn">
              <a-icon type="global" />
              <span class="lang-text">{{ $i18n.locale === 'zh-CN' ? '中' : 'EN' }}</span>
            </button>
            <a-menu slot="overlay" @click="handleLangClick" class="custom-dark-menu">
              <a-menu-item key="zh-CN">🇨🇳 简体中文</a-menu-item>
              <a-menu-item key="en-US">🇺🇸 English</a-menu-item>
            </a-menu>
          </a-dropdown>

          <template v-if="!isLoggedIn">
            <button class="btn-ghost desktop-only" @click="$router.push('/login')">{{ $t('menu.login') }}</button>
            <button class="btn-primary desktop-only" @click="$router.push('/register')">{{ $t('menu.register') }}</button>
          </template>
          <template v-else>
            <a-dropdown :trigger="['hover']" placement="bottomRight">
              <button class="user-pill desktop-only" @click="$router.push('/dashboard/auth')">
                <div class="u-avatar"><a-icon type="user" /></div>
                <span class="u-name">{{ username }}</span>
              </button>
              <a-menu slot="overlay" class="custom-dark-menu">
                <a-menu-item key="dashboard" @click="$router.push('/dashboard/auth')">
                  <a-icon type="appstore" /> {{ $t('menu.dashboard') }}
                </a-menu-item>
                <a-menu-divider />
                <a-menu-item key="logout" @click="handleLogout" class="danger-text">
                  <a-icon type="logout" /> {{ $t('menu.logout') }}
                </a-menu-item>
              </a-menu>
            </a-dropdown>
          </template>

          <button class="action-icon-btn mobile-only" @click="showMobileMenu = true">
            <a-icon type="menu" />
          </button>
        </div>
      </nav>
    </div>

    <!-- Mobile Drawer -->
    <a-drawer
      placement="right"
      :visible="showMobileMenu"
      @close="showMobileMenu = false"
      :closable="false"
      width="100%"
      wrapClassName="dark-mobile-drawer"
    >
      <div class="drawer-inner">
        <div class="d-header">
          <div class="nav-brand">
            <img src="/logo.png" alt="logo" class="brand-logo-img" />
            <span class="brand-name">spaceproxy</span>
          </div>
          <button class="close-btn" @click="showMobileMenu = false">
            <a-icon type="close" />
          </button>
        </div>
        <div class="d-menu">
          <router-link to="/" @click.native="showMobileMenu = false">{{ $t('menu.home') }}</router-link>
          <router-link to="/dynamic-residential-proxy" @click.native="showMobileMenu = false">{{ $t('menu.dynamicProxy') }}</router-link>
          <router-link to="/static-residential-proxy" @click.native="showMobileMenu = false">{{ $t('menu.staticProxyPage') }}</router-link>
          <router-link to="/help-center" @click.native="showMobileMenu = false">{{ $t('menu.helpCenter') }}</router-link>
          <a @click="toggleMobileLang"><a-icon type="global" /> {{ $i18n.locale === 'zh-CN' ? 'Switch to English' : '切换到中文' }}</a>
        </div>
        <div class="d-footer">
          <template v-if="!isLoggedIn">
            <button class="btn-ghost full-width" @click="$router.push('/login'); showMobileMenu = false">{{ $t('menu.login') }}</button>
            <button class="btn-primary full-width mt-4" @click="$router.push('/register'); showMobileMenu = false">{{ $t('menu.register') }}</button>
          </template>
          <template v-else>
            <div class="d-user">
              <div class="u-avatar"><a-icon type="user" /></div>
              <span>{{ username }}</span>
            </div>
            <button class="btn-ghost full-width mt-4" @click="$router.push('/dashboard/auth'); showMobileMenu = false">{{ $t('menu.dashboard') }}</button>
            <button class="btn-danger full-width mt-4" @click="handleLogout(); showMobileMenu = false">{{ $t('menu.logout') }}</button>
          </template>
        </div>
      </div>
    </a-drawer>

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-bg">
        <div class="glow-blob center-blob"></div>
        <div class="grid-overlay"></div>
      </div>
      <div class="container relative-z">
        <div class="badge-pill">
          <span class="dot"></span> {{ $t('menu.staticProxyPage') }}
        </div>
        <h1 class="hero-title">{{ $t('staticProxyPage.hero.title') }}</h1>
        <p class="hero-desc" v-html="$t('staticProxyPage.hero.subtitle')"></p>
        <div class="hero-actions">
          <button class="btn-primary huge" @click="$router.push('/dashboard/auth')">
            {{ $t('staticProxyPage.hero.buyNow') }} <a-icon type="arrow-right" />
          </button>
          <button class="btn-outline huge" @click="handleContactClick">
            <a-icon type="message" /> {{ $t('staticProxyPage.hero.contactSales') }}
          </button>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features-section scroll-reveal">
      <div class="container">
        <div class="section-head text-center">
          <h2>{{ $t('staticProxyPage.features.title') }}</h2>
        </div>
        
        <div class="bento-grid">
          <div class="bento-card b-card-0">
            <div class="b-icon"><a-icon type="safety-certificate" /></div>
            <div class="b-content">
              <h3>{{ $t('staticProxyPage.features.pure') }}</h3>
              <p>{{ $t('staticProxyPage.features.pureDesc') }}</p>
            </div>
            <div class="b-bg-effect"></div>
          </div>
          
          <div class="bento-card b-card-1">
            <div class="b-icon" style="color: #3b82f6;"><a-icon type="clock-circle" /></div>
            <div class="b-content">
              <h3>{{ $t('staticProxyPage.features.stable') }}</h3>
              <p>{{ $t('staticProxyPage.features.stableDesc') }}</p>
            </div>
            <div class="b-bg-effect" style="background: radial-gradient(circle at top right, rgba(59, 130, 246, 0.1), transparent 60%);"></div>
          </div>
          
          <div class="bento-card b-card-2">
            <div class="b-icon" style="color: #10b981;"><a-icon type="dashboard" /></div>
            <div class="b-content">
              <h3>{{ $t('staticProxyPage.features.unlimited') }}</h3>
              <p>{{ $t('staticProxyPage.features.unlimitedDesc') }}</p>
            </div>
            <div class="b-bg-effect" style="background: radial-gradient(circle at top right, rgba(16, 185, 129, 0.1), transparent 60%);"></div>
          </div>
          
          <div class="bento-card b-card-3">
            <div class="b-icon" style="color: #f59e0b;"><a-icon type="branches" /></div>
            <div class="b-content">
              <h3>{{ $t('staticProxyPage.features.native') }}</h3>
              <p>{{ $t('staticProxyPage.features.nativeDesc') }}</p>
            </div>
            <div class="b-bg-effect" style="background: radial-gradient(circle at top right, rgba(245, 158, 11, 0.1), transparent 60%);"></div>
          </div>
          
          <div class="bento-card b-card-1">
            <div class="b-icon" style="color: #ec4899;"><a-icon type="api" /></div>
            <div class="b-content">
              <h3>{{ $t('staticProxyPage.features.protocol') }}</h3>
              <p>{{ $t('staticProxyPage.features.protocolDesc') }}</p>
            </div>
            <div class="b-bg-effect" style="background: radial-gradient(circle at top right, rgba(236, 72, 153, 0.1), transparent 60%);"></div>
          </div>
          
          <div class="bento-card b-card-2">
            <div class="b-icon" style="color: #06b6d4;"><a-icon type="rocket" /></div>
            <div class="b-content">
              <h3>{{ $t('staticProxyPage.features.speed') }}</h3>
              <p>{{ $t('staticProxyPage.features.speedDesc') }}</p>
            </div>
            <div class="b-bg-effect" style="background: radial-gradient(circle at top right, rgba(6, 182, 212, 0.1), transparent 60%);"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section class="pricing-section scroll-reveal">
      <div class="container">
        <div class="section-head text-center">
          <h2>{{ $t('staticProxyPage.pricing.title') }}</h2>
          <p>{{ $t('staticProxyPage.pricing.desc') }}</p>
        </div>
        
        <div class="pricing-grid">
          <!-- Broadcast Plan -->
          <div class="pricing-card">
            <div class="p-header">
              <h3>{{ $t('staticProxyPage.pricing.broadcast') }}</h3>
              <p>{{ $t('staticProxyPage.pricing.broadcastDesc') }}</p>
            </div>
            <div class="p-features">
              <ul>
                <li><a-icon type="check-circle" class="check-icon" /> {{ $t('staticProxyPage.pricing.features.f1') }}</li>
                <li><a-icon type="check-circle" class="check-icon" /> {{ $t('staticProxyPage.pricing.features.f2') }}</li>
                <li><a-icon type="check-circle" class="check-icon" /> {{ $t('staticProxyPage.pricing.features.f3') }}</li>
                <li><a-icon type="check-circle" class="check-icon" /> {{ $t('staticProxyPage.pricing.features.f4') }}</li>
                <li><a-icon type="check-circle" class="check-icon" /> {{ $t('staticProxyPage.pricing.features.f5') }}</li>
              </ul>
            </div>
            <button class="btn-outline full-width" @click="$router.push('/dashboard/auth')">{{ $t('staticProxyPage.pricing.buyBtn') }}</button>
          </div>
          
          <!-- Native Plan -->
          <div class="pricing-card popular">
            <div class="popular-badge">Recommended</div>
            <div class="p-header">
              <h3>{{ $t('staticProxyPage.pricing.native') }}</h3>
              <p>{{ $t('staticProxyPage.pricing.nativeDesc') }}</p>
            </div>
            <div class="p-features">
              <ul>
                <li><a-icon type="check-circle" class="check-icon" /> {{ $t('staticProxyPage.pricing.features.f1') }}</li>
                <li><a-icon type="check-circle" class="check-icon" /> {{ $t('staticProxyPage.pricing.features.f2') }}</li>
                <li><a-icon type="check-circle" class="check-icon" /> {{ $t('staticProxyPage.pricing.features.f3') }}</li>
                <li><a-icon type="check-circle" class="check-icon" /> {{ $t('staticProxyPage.pricing.features.f4') }}</li>
                <li><a-icon type="check-circle" class="check-icon" /> {{ $t('staticProxyPage.pricing.features.f5') }}</li>
              </ul>
            </div>
            <button class="btn-primary full-width" @click="$router.push('/dashboard/auth')">{{ $t('staticProxyPage.pricing.buyBtn') }}</button>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="use-cases-section scroll-reveal">
      <div class="container">
        <div class="section-head text-center">
          <h2>{{ $t('staticProxyPage.useCases.title') }}</h2>
          <p>{{ $t('staticProxyPage.useCases.desc') }}</p>
        </div>
        
        <div class="uc-grid">
          <div class="uc-card">
            <div class="uc-icon"><a-icon type="shop" /></div>
            <h3>{{ $t('staticProxyPage.useCases.c1Title') }}</h3>
            <p>{{ $t('staticProxyPage.useCases.c1Desc') }}</p>
          </div>
          <div class="uc-card">
            <div class="uc-icon"><a-icon type="team" /></div>
            <h3>{{ $t('staticProxyPage.useCases.c2Title') }}</h3>
            <p>{{ $t('staticProxyPage.useCases.c2Desc') }}</p>
          </div>
          <div class="uc-card">
            <div class="uc-icon"><a-icon type="pay-circle" /></div>
            <h3>{{ $t('staticProxyPage.useCases.c3Title') }}</h3>
            <p>{{ $t('staticProxyPage.useCases.c3Desc') }}</p>
          </div>
          <div class="uc-card">
            <div class="uc-icon"><a-icon type="bar-chart" /></div>
            <h3>{{ $t('staticProxyPage.useCases.c4Title') }}</h3>
            <p>{{ $t('staticProxyPage.useCases.c4Desc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="dark-footer">
      <div class="container">
        <div class="f-grid">
          <div class="f-brand">
            <div class="nav-brand">
              <img src="/logo.png" alt="logo" class="brand-logo-img" />
              <span class="brand-name">spaceproxy</span>
            </div>
            <p>{{ $t('home.footer.desc') }}</p>
          </div>
          <div class="f-links">
            <div class="f-col">
              <h4>{{ $t('home.footer.products') }}</h4>
              <router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link>
              <router-link to="/static-residential-proxy">{{ $t('menu.staticProxyPage') }}</router-link>
            </div>
            <div class="f-col">
              <h4>{{ $t('home.footer.support') }}</h4>
              <router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link>
            </div>
            <div class="f-col">
              <h4>{{ $t('home.footer.contact') }}</h4>
              <div class="contact-item">
                <a-icon type="wechat" />
                <span></span>
              </div>
              <!-- <div class="contact-item">
                <a-icon type="qq" />
                <span>QQ: 1337007470</span>
              </div> -->
            </div>
          </div>
        </div>
        <div class="f-bottom">
          <p>{{ $t('home.footer.copyright', { year: new Date().getFullYear() }) }}</p>
        </div>
      </div>
    </footer>

    <!-- Back Top -->
    <a-back-top :visibilityHeight="300">
      <div class="back-top-btn">
        <a-icon type="arrow-up" />
      </div>
    </a-back-top>
  </div>
</template>

<script>
export default {
  name: 'StaticResidentialProxy',
  data() {
    return {
      isLoggedIn: false,
      username: '',
      showMobileMenu: false,
      isScrolled: false
    };
  },
  mounted() {
    this.checkLoginStatus();
    this.initScrollReveal();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    handleLangClick({ key }) {
      this.$i18n.locale = key;
      localStorage.setItem('language', key);
    },
    toggleMobileLang() {
      const newLang = this.$i18n.locale === 'zh-CN' ? 'en-US' : 'zh-CN';
      this.$i18n.locale = newLang;
      localStorage.setItem('language', newLang);
    },
    handleContactClick() {
      this.$message.info('请联系');
    },
    checkLoginStatus() {
      const token = localStorage.getItem('Token');
      const username = localStorage.getItem('userName');
      if (token && username) {
        this.isLoggedIn = true;
        this.username = username;
      }
    },
    handleLogout() {
      localStorage.clear();
      this.isLoggedIn = false;
      this.username = '';
      this.$message.success('退出登录成功');
      this.$router.push('/login');
    },
    initScrollReveal() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      setTimeout(() => {
        const elements = document.querySelectorAll('.scroll-reveal');
        elements.forEach(el => {
          el.style.opacity = '0';
          el.style.transform = 'translateY(40px)';
          el.style.transition = 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)';
          observer.observe(el);
        });
      }, 100);
    }
  }
};
</script>

<style scoped lang="less">
/* Variables */
@bg-dark: #09090b;
@card-bg: #18181b;
@border: #27272a;
@primary: #8b5cf6;
@primary-hover: #a855f7;
@accent: #3b82f6;
@text-main: #f4f4f5;
@text-muted: #a1a1aa;

.static-proxy-page {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  background-color: @bg-dark;
  color: @text-main;
  min-height: 100vh;
  overflow-x: hidden;
}

button {
  outline: none;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Animations */
.scroll-reveal.is-visible {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

/* Navigation */
.nav-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 20px;
  transition: all 0.4s ease;
  
  &.scrolled {
    padding: 10px 20px;
    
    .modern-nav {
      background: rgba(24, 24, 27, 0.8);
      backdrop-filter: blur(12px);
      border-color: @border;
      box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    }
  }
}

.modern-nav {
  max-width: 1200px;
  margin: 0 auto;
  height: 64px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  transition: all 0.4s ease;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  
  .brand-logo-img {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    object-fit: cover;
  }
  
  .brand-icon {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, @primary, @accent);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    svg { width: 20px; height: 20px; }
  }
  
  .brand-name {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.5px;
    color: @text-main;
  }
}

.nav-links {
  display: flex;
  gap: 32px;
  
  a {
    color: @text-muted;
    font-weight: 500;
    font-size: 15px;
    transition: color 0.3s;
    
    &:hover, &.router-link-exact-active {
      color: @text-main;
    }
  }
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-icon-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  color: @text-muted;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.3s;
  
  &:hover {
    background: @card-bg;
    color: @text-main;
  }
}

.btn-ghost {
  color: @text-main;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  transition: all 0.3s;
  &:hover { background: @card-bg; }
}

.btn-primary {
  background: linear-gradient(135deg, @primary, @accent);
  color: #fff;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 10px;
  transition: all 0.3s;
  box-shadow: 0 4px 14px rgba(139, 92, 246, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
  }
  
  &.huge {
    padding: 16px 36px;
    font-size: 18px;
    border-radius: 14px;
  }
  
  &.full-width {
    width: 100%;
  }
}

.btn-outline {
  border: 1px solid @border;
  background: @card-bg;
  color: @text-main;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 10px;
  transition: all 0.3s;
  
  &:hover {
    border-color: @text-muted;
    background: rgba(255,255,255,0.05);
  }
  
  &.huge {
    padding: 16px 36px;
    font-size: 18px;
    border-radius: 14px;
  }
  
  &.full-width {
    width: 100%;
  }
}

.user-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: @card-bg;
  border: 1px solid @border;
  padding: 4px 16px 4px 4px;
  border-radius: 20px;
  color: @text-main;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
  
  .u-avatar {
    width: 28px;
    height: 28px;
    background: @primary;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
  
  &:hover {
    border-color: @primary;
  }
}

.mobile-only { display: none; }

/* Sections Global */
.section-head {
  margin-bottom: 60px;
  
  h2 {
    font-size: 40px;
    font-weight: 800;
    color: #fff;
    margin: 0;
    letter-spacing: -1px;
  }
  
  p {
    color: @text-muted;
    font-size: 18px;
    margin-top: 16px;
  }
  
  &.text-center { text-align: center; }
}

/* Hero Section */
.hero-section {
  position: relative;
  padding: 200px 0 120px;
  text-align: center;
  border-bottom: 1px solid @border;
  overflow: hidden;
  
  .hero-bg {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    overflow: hidden;
    z-index: 0;
    
    .glow-blob {
      position: absolute;
      width: 800px;
      height: 800px;
      border-radius: 50%;
      filter: blur(120px);
      opacity: 0.15;
      background: @accent; /* 静态代理主打蓝色系点缀 */
      top: -300px;
      left: 50%;
      transform: translateX(-50%);
    }
    
    .grid-overlay {
      position: absolute;
      inset: 0;
      background-image: 
        linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px);
      background-size: 50px 50px;
      mask-image: radial-gradient(ellipse at center, black 0%, transparent 80%);
    }
  }
  
  .relative-z { position: relative; z-index: 1; }
  
  .badge-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.2);
    color: @accent;
    padding: 6px 16px;
    border-radius: 100px;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 32px;
    
    .dot {
      width: 8px;
      height: 8px;
      background: @accent;
      border-radius: 50%;
      box-shadow: 0 0 10px @accent;
    }
  }
  
  .hero-title {
    font-size: 64px;
    font-weight: 900;
    color: #fff;
    margin-bottom: 24px;
    letter-spacing: -1px;
    line-height: 1.1;
  }
  
  .hero-desc {
    font-size: 20px;
    color: @text-muted;
    line-height: 1.6;
    max-width: 800px;
    margin: 0 auto 48px;
  }
  
  .hero-actions {
    display: flex;
    justify-content: center;
    gap: 20px;
  }
}

/* Bento Features */
.features-section { padding: 100px 0; }

.bento-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 240px;
  gap: 24px;
  
  .bento-card {
    background: @card-bg;
    border: 1px solid @border;
    border-radius: 24px;
    padding: 32px;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all 0.4s;
    
    &:hover {
      border-color: rgba(59, 130, 246, 0.4);
      transform: translateY(-5px);
      
      .b-bg-effect { opacity: 1; }
      .b-icon { background: @accent; color: #fff !important; }
    }
    
    .b-icon {
      width: 48px;
      height: 48px;
      background: rgba(255,255,255,0.05);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      color: @primary;
      margin-bottom: auto;
      transition: all 0.4s;
      z-index: 1;
    }
    
    .b-content {
      z-index: 1;
      h3 { font-size: 20px; font-weight: 700; color: #fff; margin-bottom: 8px; }
      p { color: @text-muted; font-size: 15px; margin: 0; line-height: 1.5; }
    }
    
    .b-bg-effect {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: radial-gradient(circle at top right, rgba(139, 92, 246, 0.1), transparent 60%);
      opacity: 0;
      transition: opacity 0.4s;
      z-index: 0;
    }
  }
  
  .b-card-0 { grid-column: span 2; }
  .b-card-1 { grid-column: span 1; }
  .b-card-2 { grid-column: span 1; }
  .b-card-3 { grid-column: span 2; }
}

/* Pricing */
.pricing-section {
  padding: 100px 0;
  background: #000;
  border-top: 1px solid @border;
  border-bottom: 1px solid @border;
}

.pricing-grid {
  display: flex;
  justify-content: center;
  gap: 32px;
  max-width: 900px;
  margin: 0 auto;
}

.pricing-card {
  flex: 1;
  background: @card-bg;
  border: 1px solid @border;
  border-radius: 24px;
  padding: 40px;
  position: relative;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(255,255,255,0.1);
  }
  
  &.popular {
    border-color: @accent;
    background: linear-gradient(180deg, rgba(59, 130, 246, 0.05) 0%, @card-bg 100%);
    
    &:hover {
      box-shadow: 0 10px 40px rgba(59, 130, 246, 0.15);
    }
  }
  
  .popular-badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: @accent;
    color: #fff;
    padding: 4px 16px;
    border-radius: 100px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .p-header {
    text-align: center;
    margin-bottom: 32px;
    padding-bottom: 32px;
    border-bottom: 1px solid @border;
    
    h3 {
      font-size: 24px;
      font-weight: 700;
      color: #fff;
      margin-bottom: 12px;
    }
    
    p {
      color: @text-muted;
      font-size: 15px;
      margin: 0;
    }
  }
  
  .p-features {
    margin-bottom: 40px;
    
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 16px;
      
      li {
        display: flex;
        align-items: center;
        gap: 12px;
        color: @text-main;
        font-size: 15px;
        
        .check-icon {
          color: @accent;
          font-size: 18px;
        }
      }
    }
  }
}

/* Use Cases */
.use-cases-section {
  padding: 100px 0;
}

.uc-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 1000px;
  margin: 0 auto;
}

.uc-card {
  background: @card-bg;
  border: 1px solid @border;
  border-radius: 20px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s;
  
  &:hover {
    border-color: @accent;
    background: rgba(255,255,255,0.02);
    
    .uc-icon {
      background: @accent;
      color: #fff;
    }
  }
  
  .uc-icon {
    width: 56px;
    height: 56px;
    background: rgba(59, 130, 246, 0.1);
    color: @accent;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    margin-bottom: 24px;
    transition: all 0.3s;
  }
  
  h3 {
    font-size: 20px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 12px;
  }
  
  p {
    color: @text-muted;
    font-size: 15px;
    line-height: 1.6;
    margin: 0;
  }
}

/* Footer */
.dark-footer {
  background: #000; border-top: 1px solid @border; padding: 80px 0 40px;
  
  .f-grid {
    display: flex; justify-content: space-between; gap: 40px; margin-bottom: 60px;
  }
  
  .f-brand {
    max-width: 300px;
    p { color: @text-muted; line-height: 1.6; margin-top: 20px; }
  }
  
  .f-links {
    display: flex; gap: 80px;
    
    .f-col {
      display: flex; flex-direction: column; gap: 16px;
      h4 { color: #fff; font-weight: 700; font-size: 16px; margin: 0; }
      a { color: @text-muted; transition: color 0.3s; &:hover { color: @primary; } }
      .contact-item {
        display: flex;
        align-items: center;
        gap: 8px;
        color: @text-muted;
        font-size: 14px;
        i { color: @primary; font-size: 16px; }
      }
    }
  }
  
  .f-bottom {
    border-top: 1px solid @border; padding-top: 30px; text-align: center;
    p { color: @text-muted; font-size: 14px; margin: 0; }
  }
}

.back-top-btn {
  width: 50px; height: 50px;
  background: @card-bg; border: 1px solid @border; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: @text-main; font-size: 20px;
  transition: all 0.3s;
  &:hover { background: @primary; border-color: @primary; color: #fff; }
}

/* Responsive */
@media (max-width: 1024px) {
  .bento-grid {
    grid-template-columns: 1fr 1fr;
    .b-card-0, .b-card-3 { grid-column: span 2; }
    .b-card-1, .b-card-2 { grid-column: span 1; }
  }
  .pricing-grid { flex-direction: column; max-width: 500px; }
  .f-grid { flex-direction: column; }
  .f-links { flex-wrap: wrap; gap: 40px; }
}

@media (max-width: 768px) {
  .desktop-only { display: none !important; }
  .mobile-only { display: flex !important; }
  
  .nav-container { padding: 10px; }
  .modern-nav { height: 56px; padding: 0 16px; border-radius: 12px; background: rgba(24, 24, 27, 0.9); }
  
  .hero-section { padding: 120px 24px 60px; }
  .hero-title { font-size: 40px; }
  .hero-desc { font-size: 16px; }
  .hero-actions { flex-direction: column; gap: 16px; button { width: 100%; } }
  
  .bento-grid {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    .b-card-0, .b-card-1, .b-card-2, .b-card-3 { grid-column: span 1; }
  }
  
  .uc-grid { grid-template-columns: 1fr; }
  .f-links { gap: 30px; flex-direction: column; }
}
</style>
